import { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Html5Qrcode } from "html5-qrcode";
import { Loader2 } from "lucide-react";

// Modal de escaneo de QR con la cámara del dispositivo (usado para autorellenar el
// formulario de check-in del Punto Limpio con los datos básicos del ciudadano).
export default function QrScannerModal({ open, onClose, onResult, title = "Escanea tu código QR" }) {
  const regionId = "qr-scanner-region";
  const scannerRef = useRef(null);
  const [error, setError] = useState("");
  const [regionNode, setRegionNode] = useState(null);

  // Callback ref: solo sabemos con certeza que el <div> existe en el DOM (portal de
  // Radix montado) cuando React invoca esta función, evitando la carrera con Html5Qrcode.
  const regionRef = useCallback((node) => {
    setRegionNode(node);
  }, []);

  useEffect(() => {
    if (!open || !regionNode) return;
    setError("");
    let scanner;
    let stopped = false;
    let cancelled = false;
    try {
      scanner = new Html5Qrcode(regionId);
    } catch {
      setError("No se pudo inicializar el lector de QR en este dispositivo.");
      return;
    }
    scannerRef.current = scanner;
    // Evita parar la cámara dos veces a la vez (una desde el propio éxito de lectura,
    // otra desde la limpieza de React al cerrarse el modal), que con html5-qrcode lanza
    // un error no controlado y activaba la pantalla de error genérica de la app.
    const safeStop = () => {
      if (stopped) return Promise.resolve();
      stopped = true;
      try {
        return scanner.stop().catch(() => {});
      } catch {
        return Promise.resolve();
      }
    };
    const startPromise = scanner
      .start(
        { facingMode: "environment" },
        { fps: 10, qrbox: 230 },
        (decodedText) => {
          if (cancelled) return;
          safeStop().finally(() => {
            if (!cancelled) onResult(decodedText);
          });
        },
        () => {}
      )
      .catch(() => setError("No se pudo acceder a la cámara. Revisa los permisos del navegador."));

    return () => {
      // Si el modal se cierra mientras start() todavía está pidiendo permiso de
      // cámara, hay que esperar a que termine antes de parar: pararla a mitad de
      // arranque también lanza un error no controlado en html5-qrcode.
      cancelled = true;
      startPromise.then(safeStop);
    };
  }, [open, regionNode, onResult]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="qr-scanner-modal">
        <DialogHeader><DialogTitle className="text-municipal font-heading">{title}</DialogTitle></DialogHeader>
        <div id={regionId} ref={regionRef} className="rounded-lg overflow-hidden" data-testid="qr-scanner-region" />
        {!error && <div className="flex justify-center py-2"><Loader2 className="w-4 h-4 animate-spin text-gray-400" /></div>}
        {error && <p className="text-sm text-red-500 text-center">{error}</p>}
      </DialogContent>
    </Dialog>
  );
}

